import React from 'react'
import { Box, Checkbox } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { __t } from '../../../../../i18n/t'
import { centsFrom } from '../../format'
import {
  CONFIRM_CENTS, FuelRow, MAX_CENTS, PointsMode, QUICK_CENTS, WARN_POINTS, activeRows, addOpt, applyAll, brl, digits, fmtCents, fmtN,
  fuelRows, multiOptNote, ptsErr, ptsOf, removeOpt, selIds, setOptCents, showsPoints, toggleFuel, updateOpt, valErr,
} from '../../model'
import { useWiz } from '../../state/WizardContext'
import { Ask, Callout, FieldError, Hint } from '../../ui/basics'
import { CheckRow, LabeledInput, OutlineButton, Pill, Seg, TextButton } from '../../ui/controls'
import { C, CALLOUT, warnBoxSx, warnLineSx } from '../../ui/tokens'

// Estilo fora do JSX: o plugin de i18n embrulha literal de texto dentro de `&&`/ternário (ver ui/tokens.ts).
const optionHeadSx = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 } as const
const optionTitleSx = { fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted } as const
const optionRowSx = { display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' } as const
const costColumnSx = { display: 'flex', flexDirection: 'column', gap: 0.5 } as const
const costLabelSx = { fontSize: 12.5, fontWeight: 500, color: C.muted } as const
const warnTopSx = { display: 'flex', gap: 0.75, alignItems: 'center' } as const
const statusSx = { display: 'flex', flexDirection: 'column', gap: 0.75 } as const
const optionsColumnSx = { flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 } as const
const noFuelSx = { pt: 1.25, fontSize: 13.5, color: C.muted } as const
const emptyBoxSx = { p: 2.5, border: '1.5px dashed #94a3b8', borderRadius: '12px', fontSize: 14, color: C.muted, textAlign: 'center', maxWidth: 760 } as const
const sameBoxSx = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, p: '12px 14px', bgcolor: C.surface, border: `1px solid ${C.line}`, borderRadius: '12px', maxWidth: 820,
} as const
const groupSx = { border: `1px solid ${C.line}`, borderRadius: '12px', overflow: 'hidden', maxWidth: 860 } as const
const fuelLabelSx = {
  display: 'flex', alignItems: 'flex-start', gap: 1.25, flex: { xs: '1 1 100%', sm: '0 0 210px' }, maxWidth: '100%', pt: 1, cursor: 'pointer', minHeight: 40,
  '&:has(input:focus-visible)': { outline: `2px solid ${C.violet}`, outlineOffset: 2, borderRadius: '6px' },
} as const
const fuelNameSx = { fontSize: 15, fontWeight: 500, color: C.ink, overflowWrap: 'anywhere' } as const

/** Volta o foco para "Outra opção para …" depois de remover uma opção (o botão de remover some e o foco não pode ir para o fim da página). */
const focusAddOption = (fuelId: string) => { window.setTimeout(() => document.getElementById(`add-opt-${fuelId}`)?.focus(), 0) }

/** Grátis / Gasta pontos e o campo dos pontos de uma opção. */
const CostControls: React.FC<{ row: FuelRow; index: number; ptsId: string; showPe: boolean }> = ({ row, index, ptsId, showPe }) => {
  const { c, set } = useWiz()
  const o = row.opts[index]
  const fuel = c.fuels.find(f => f.id === row.id)!
  return (
    <>
      <Box sx={costColumnSx}>
        <Box component="span" sx={costLabelSx}>{__t('Custo em pontos')}</Box>
        <Seg
          value={o.payPoints ? PointsMode.pay : PointsMode.free}
          onChange={v => set(x => updateOpt(x, fuel, index, { payPoints: v === PointsMode.pay }))}
          label={__t('Custo em pontos, %{combustivel}', { combustivel: row.name })}
          options={[{ value: PointsMode.free, label: __t('Grátis') }, { value: PointsMode.pay, label: __t('Gasta pontos') }]}
        />
      </Box>
      {o.payPoints ? (
        <LabeledInput
          id={ptsId}
          label={__t('Pontos por abastecimento')}
          suffix={__t('pontos')}
          width={170}
          inputMode="numeric"
          placeholder="100"
          value={o.pts}
          onChange={e => set(x => updateOpt(x, fuel, index, { pts: digits(e.target.value, 5) }))}
          invalid={showPe}
          describedBy={showPe ? `${ptsId}-err` : undefined}
        />
      ) : null}
    </>
  )
}

/** Pedido de confirmação de um valor de R$ 1,00 ou mais por litro. */
const HighValueConfirm: React.FC<{ row: FuelRow; index: number; highId: string; okId: string }> = ({ row, index, highId, okId }) => {
  const { c, set } = useWiz()
  const o = row.opts[index]
  const fuel = c.fuels.find(f => f.id === row.id)!
  return (
    <Box sx={warnBoxSx}>
      <Box id={highId} component="span" sx={warnTopSx}>
        <WarningAmberIcon aria-hidden sx={{ fontSize: 18, color: CALLOUT.warnIcon, flexShrink: 0 }} />
        {__t('%{valor} por litro é um desconto alto.', { valor: brl(o.cents ?? 0) })}
      </Box>
      <CheckRow id={okId} checked={o.okHigh} onChange={() => set(x => updateOpt(x, fuel, index, { okHigh: !o.okHigh }))}>
        {__t('O valor está certo')}
      </CheckRow>
    </Box>
  )
}

/** Uma opção de desconto: valor por litro + custo em pontos + frase que explica o que o cliente vê. */
const OptionEditor: React.FC<{ row: FuelRow; index: number }> = ({ row, index }) => {
  const { d, c, set, fe } = useWiz()
  const o = row.opts[index]
  const multi = row.opts.length > 1
  const id = `${row.id}-${index}`
  const valId = `f-val-${id}`
  const ptsId = `f-pts-${id}`
  const highId = `f-high-${id}`
  const okId = `f-ok-${id}`
  const showVe = !!fe[valId]
  const showPe = !!fe[ptsId]
  const ve = valErr(o)
  const pe = ptsErr(d, o)
  const high = (o.cents ?? 0) >= CONFIRM_CENTS && (o.cents ?? 0) <= MAX_CENTS
  const pts = ptsOf(d, o)
  const fuel = c.fuels.find(f => f.id === row.id)!
  const pointsOn = showsPoints(d.kind)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: multi ? 1.5 : 0, border: multi ? `1px solid ${C.line}` : 0, borderRadius: '10px', bgcolor: '#fff' }}>
      {multi ? (
        <Box sx={optionHeadSx}>
          <Box component="span" sx={optionTitleSx}>{__t('Opção %{n}', { n: index + 1 })}</Box>
          {index > 0 ? (
            <TextButton
              onClick={() => { set(x => removeOpt(x, fuel, index)); focusAddOption(row.id) }}
              color={C.red}
              icon={<DeleteOutlineIcon />}
              ariaLabel={__t('Remover opção %{n} de %{combustivel}', { n: index + 1, combustivel: row.name })}
            >
              {__t('Remover')}
            </TextButton>
          ) : null}
        </Box>
      ) : null}
      <Box sx={optionRowSx}>
        <LabeledInput
          id={valId}
          label={__t('Desconto por litro')}
          prefix="R$"
          width={150}
          inputMode="numeric"
          placeholder="0,00"
          value={o.cents == null ? '' : fmtCents(o.cents)}
          onChange={e => set(x => setOptCents(x, fuel, index, centsFrom(e.target.value)))}
          invalid={showVe}
          describedBy={[high ? highId : '', showVe ? `${valId}-err` : ''].filter(Boolean).join(' ') || undefined}
        />
        {pointsOn ? <CostControls row={row} index={index} ptsId={ptsId} showPe={showPe} /> : null}
      </Box>
      <Box role="status" sx={statusSx}>
        {high ? <HighValueConfirm row={row} index={index} highId={highId} okId={okId} /> : null}
        {o.cents === 1 ? <WarnLine>{__t('R$ 0,01 por litro é muito pouco. Você quis dizer R$ 0,10? Digite 10.')}</WarnLine> : null}
        {pts > WARN_POINTS ? <WarnLine>{__t('%{pontos} pontos por abastecimento é um custo alto. Confira o valor.', { pontos: fmtN(pts) })}</WarnLine> : null}
      </Box>
      {showVe ? <FieldError id={`${valId}-err`}>{ve}</FieldError> : null}
      {showPe ? <FieldError id={`${ptsId}-err`}>{pe}</FieldError> : null}
    </Box>
  )
}

const WarnLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={warnLineSx}>
    <WarningAmberIcon aria-hidden sx={{ fontSize: 16, color: CALLOUT.warnIcon, mt: '1px', flexShrink: 0 }} />
    <span>{children}</span>
  </Box>
)

/** As opções de um combustível marcado e o botão "Outra opção para …". */
const FuelOptions: React.FC<{ row: FuelRow }> = ({ row }) => {
  const { d, c, set } = useWiz()
  const fuel = c.fuels.find(f => f.id === row.id)!
  return (
    <Box sx={optionsColumnSx}>
      {row.opts.map((_, i) => <OptionEditor key={i} row={row} index={i} />)}
      {row.opts.length < 3 ? (
        <TextButton id={`add-opt-${row.id}`} onClick={() => set(x => addOpt(x, fuel))} icon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}>
          {__t('Outra opção para %{combustivel}', { combustivel: row.name })}
        </TextButton>
      ) : null}
      {row.opts.length > 1 && multiOptNote(d, row) ? <Hint sx={{ fontSize: 12.5 }}>{multiOptNote(d, row)}</Hint> : null}
    </Box>
  )
}

const FuelRowView: React.FC<{ row: FuelRow; first: boolean }> = ({ row, first }) => {
  const { c, set } = useWiz()
  const fuel = c.fuels.find(f => f.id === row.id)!
  const checkId = `fuel-${row.id}`
  return (
    <Box sx={{ borderTop: first ? 0 : `1px solid ${C.line}`, bgcolor: row.on ? '#fff' : '#fafbfc', p: '14px 16px' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px 16px', alignItems: 'flex-start' }}>
        <Box component="label" htmlFor={checkId} sx={fuelLabelSx}>
          <Checkbox id={checkId} checked={row.on} onChange={() => set(x => toggleFuel(x, fuel))} disableRipple sx={{ p: 0, mt: '1px', color: C.inputBorder, '&.Mui-checked': { color: C.violet }, '& .MuiSvgIcon-root': { fontSize: 22 } }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Box component="span" sx={fuelNameSx}>{row.name}</Box>
            {row.total > 1 && row.n < row.total ? <Hint sx={{ fontSize: 12.5 }}>{__t('Vendido em %{n} de %{total} postos', { n: row.n, total: row.total })}</Hint> : null}
          </Box>
        </Box>
        {!row.on ? <Box component="span" sx={noFuelSx}>{__t('Sem desconto neste combustível')}</Box> : null}
        {row.on ? <FuelOptions row={row} /> : null}
      </Box>
    </Box>
  )
}

/** "Valor igual em todos": atalhos de valor e um campo livre, para os combustíveis marcados. */
const SameValue: React.FC<{ firsts: (number | null)[]; apply: (cents: number) => void }> = ({ firsts, apply }) => {
  const { sameText, setSameText } = useWiz()
  return (
    <Box sx={sameBoxSx}>
      <Box component="span" sx={{ fontSize: 14, fontWeight: 500, color: C.ink }}>{__t('Mesmo desconto em todos os marcados:')}</Box>
      <Box role="group" aria-label={__t('Valores rápidos')} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {QUICK_CENTS.map(cents => (
          <Pill key={cents} on={firsts.length > 0 && firsts.every(v => v === cents)} onClick={() => apply(cents)}>{brl(cents)}</Pill>
        ))}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
        <Box component="span" aria-hidden sx={{ fontSize: 14, color: C.muted, pb: 1.25 }}>{__t('ou')}</Box>
        <LabeledInput
          id="same-val"
          label={__t('Valor para todos os combustíveis marcados')}
          hideLabel
          prefix="R$"
          width={120}
          inputMode="numeric"
          placeholder="0,00"
          value={sameText}
          onChange={e => { const cents = centsFrom(e.target.value); setSameText(cents == null ? '' : fmtCents(cents)) }}
        />
        <OutlineButton onClick={() => { const cents = centsFrom(sameText); if (cents) apply(cents) }} sx={{ height: 40, px: 1.5, fontSize: 14 }}>{__t('Aplicar')}</OutlineButton>
      </Box>
    </Box>
  )
}

/** "Quanto de desconto em cada combustível?": valores rápidos + uma linha por combustível dos postos escolhidos. */
export const FuelsSection: React.FC = () => {
  const { d, c, set, fe, setSameText } = useWiz()
  const rows = fuelRows(d, c)
  const active = activeRows(d, c)
  const hasStations = selIds(d, c).length > 0
  const firsts = active.map(r => r.opts[0]?.cents ?? null)
  const apply = (cents: number) => { set(x => applyAll(x, c, cents)); setSameText(fmtCents(cents)) }

  return (
    <Ask id="h-fuels" wrapId="f-fuels" title={__t('Quanto de desconto por litro?')}>
      {!hasStations ? (
        <Box sx={emptyBoxSx}>
          {__t('Escolha pelo menos um posto para ver os combustíveis.')}
        </Box>
      ) : rows.length === 0 ? (
        <Callout tone="warn" sx={{ maxWidth: 760 }}>
          {__t('Os postos escolhidos ainda não têm combustíveis cadastrados. Cadastre os combustíveis no posto para criar o desconto.')}
        </Callout>
      ) : (
        <>
          {active.length > 1 ? <SameValue firsts={firsts} apply={apply} /> : null}
          <Box role="group" aria-labelledby="h-fuels" sx={groupSx}>
            {rows.map((row, i) => <FuelRowView key={row.id} row={row} first={i === 0} />)}
          </Box>
          {fe['f-fuels'] ? <FieldError id="f-fuels-err">{fe['f-fuels']}</FieldError> : null}
        </>
      )}
    </Ask>
  )
}
